/**
 * Shared sound volume controls for SettingsMenu (battle gear) and SoundSettingsMenu (speaker icon).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMusic } from './MusicContext';

function playPreview(path, volume) {
  if (volume <= 0) return;
  try {
    const audio = new Audio(path);
    audio.volume = Math.min(1, volume);
    audio.play().catch((err) => {
      console.debug('Sound preview play failed:', err);
    });
  } catch (error) {
    console.debug('Sound preview error:', error);
  }
}

/**
 * @param {{ variant?: 'settings' | 'sound' }} props
 */
export function SoundSettingsControls({ variant = 'settings' }) {
  const { t } = useTranslation();
  const isSoundMenu = variant === 'sound';
  const prefix = isSoundMenu ? 'sound-settings' : 'settings-menu';
  const blockClass = isSoundMenu ? `${prefix}-section` : `${prefix}-subsection`;
  const headerClass = isSoundMenu ? `${prefix}-section-header` : `${prefix}-subsection-header`;
  const {
    musicEnabled,
    musicVolume,
    soundsEnabled,
    soundsVolume,
    speechVolume,
    masterVolume,
    toggleMusic,
    toggleSounds,
    toggleAll,
    setMusicVolume,
    setSoundsVolume,
    setSpeechVolume,
    setMasterVolume,
  } = useMusic();

  return (
    <>
      <div className={blockClass} data-sound-settings-section="music">
        <div className={headerClass}>
          <label className={`${prefix}-toggle`}>
            <input type="checkbox" checked={musicEnabled} onChange={toggleMusic} />
            <span>{t('soundSettings.music', 'Music')}</span>
          </label>
        </div>
        {musicEnabled && (
          <div className={`${prefix}-volume`}>
            <label>
              {t('soundSettings.volume', 'Volume')}: {Math.round(musicVolume * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={musicVolume}
              onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
              className={`${prefix}-slider`}
            />
          </div>
        )}
      </div>

      <div className={blockClass} data-sound-settings-section="sfx">
        <div className={headerClass}>
          <label className={`${prefix}-toggle`}>
            <input type="checkbox" checked={soundsEnabled} onChange={toggleSounds} />
            <span>{t('soundSettings.sounds', 'Sound Effects')}</span>
          </label>
        </div>
        {soundsEnabled && (
          <div className={`${prefix}-volume`}>
            <label>
              {t('soundSettings.volume', 'Volume')}: {Math.round(soundsVolume * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={soundsVolume}
              onChange={(e) => setSoundsVolume(parseFloat(e.target.value))}
              onMouseUp={(e) => {
                const currentVolume = parseFloat(e.target.value);
                if (soundsEnabled && currentVolume > 0 && masterVolume > 0) {
                  playPreview('/sounds/damage.mp3', masterVolume * currentVolume);
                }
              }}
              onTouchEnd={(e) => {
                const currentVolume = parseFloat(e.target.value);
                if (soundsEnabled && currentVolume > 0 && masterVolume > 0) {
                  playPreview('/sounds/damage.mp3', masterVolume * currentVolume);
                }
              }}
              className={`${prefix}-slider`}
            />
          </div>
        )}

        <div
          className={`${prefix}-nested-child`}
          data-sound-settings-section="speech"
        >
          <div className={`${prefix}-nested-header`}>
            {t('soundSettings.speech', 'Unit Speech')}
          </div>
          {soundsEnabled ? (
            <div className={`${prefix}-volume ${prefix}-volume-nested`}>
              <label>
                {t('soundSettings.volume', 'Volume')}: {Math.round(speechVolume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={speechVolume}
                onChange={(e) => setSpeechVolume(parseFloat(e.target.value))}
                onMouseUp={(e) => {
                  const currentVolume = parseFloat(e.target.value);
                  if (soundsEnabled && currentVolume > 0 && masterVolume > 0) {
                    playPreview('/sounds/id3.mp3', masterVolume * currentVolume);
                  }
                }}
                onTouchEnd={(e) => {
                  const currentVolume = parseFloat(e.target.value);
                  if (soundsEnabled && currentVolume > 0 && masterVolume > 0) {
                    playPreview('/sounds/id3.mp3', masterVolume * currentVolume);
                  }
                }}
                className={`${prefix}-slider`}
                data-testid="sound-settings-speech-volume"
              />
            </div>
          ) : (
            <p className={`${prefix}-hint ${prefix}-hint-nested`}>
              {t('soundSettings.speechRequiresSounds', 'Enable sound effects to adjust unit speech volume')}
            </p>
          )}
        </div>
      </div>

      <div className={blockClass} data-sound-settings-section="master">
        <div className={headerClass}>
          <label className={`${prefix}-toggle`}>
            <input
              type="checkbox"
              checked={musicEnabled && soundsEnabled}
              onChange={toggleAll}
            />
            <span>{t('soundSettings.all', 'All Sounds')}</span>
          </label>
        </div>
        <div className={`${prefix}-volume`}>
          <label>
            {t('soundSettings.masterVolume', 'Master volume')}: {Math.round(masterVolume * 100)}%
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={masterVolume}
            onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
            onMouseUp={(e) => {
              const m = parseFloat(e.target.value);
              if (!soundsEnabled || soundsVolume <= 0 || m <= 0) return;
              playPreview('/sounds/damage.mp3', m * soundsVolume);
            }}
            onTouchEnd={(e) => {
              const m = parseFloat(e.target.value);
              if (!soundsEnabled || soundsVolume <= 0 || m <= 0) return;
              playPreview('/sounds/damage.mp3', m * soundsVolume);
            }}
            className={`${prefix}-slider`}
          />
        </div>
      </div>
    </>
  );
}

export default SoundSettingsControls;
